import { useState, useEffect, useCallback } from 'react';
import { Layout } from '@/shared/components/Layout';
import { BackButton } from '@/shared/components/BackButton';
import { api } from '@/shared/lib/api';
import { captureError } from '@/shared/lib/monitoring';
import { useBackHandler } from '@/shared/lib/navigation/BackNavigationContext';
import { useOnTabActive, useIsTabActive } from '@/shared/components/MainTabsKeepAlive';
import { SprintRunner } from '@/features/grammar/SprintRunner';
import { AiTutorBox } from '@/features/grammar/AiTutorBox';
import type {
  GrammarTopic,
  GrammarCategory,
  GrammarProgressResponse,
  UserGrammarProgressDto,
  SprintCompleteResult,
} from '@/features/grammar/grammarTypes';

// =====================
// Types
// =====================

interface Theory {
  title: string;
  rules: Array<{ rule: string; example: string }>;
  tip: string;
}

type ExerciseType = 'fill_blanks' | 'mc_quiz' | 'sentence_scramble' | 'correct_incorrect';

interface Exercise {
  topic_id: string;
  type: ExerciseType;
  data: any;
}

// =====================
// Category List
// =====================

function CategoryList({ categories, progress, onSelectTopic }: {
  categories: GrammarCategory[];
  progress: GrammarProgressResponse | null;
  onSelectTopic: (topic: GrammarTopic) => void;
}) {
  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <div style={{ padding: '20px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <h1 style={{ margin: 0, fontSize: 22, color: 'var(--theme-text)' }}>Грамматика</h1>
        {progress && progress.total_stars > 0 && (
          <div style={{
            background: 'rgba(245, 158, 11, 0.15)',
            border: '1px solid rgba(245, 158, 11, 0.35)',
            padding: '4px 10px',
            borderRadius: 999,
            fontSize: 12,
            fontWeight: 700,
            color: '#f59e0b',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}>
            <span>⭐ {progress.total_stars} звёзд</span>
            {progress.mastered_count > 0 && <span>• 🏆 {progress.mastered_count}</span>}
          </div>
        )}
      </div>
      <p style={{ margin: '0 0 20px', fontSize: 13, color: 'var(--theme-text-secondary)' }}>
        3 ступени мастерства в каждой теме: База → Конструкции → Ловушки времён
      </p>

      <div style={{ display: 'grid', gap: 8 }}>
        {categories.map(cat => {
          const catProg = progress?.categories?.[cat.id];
          const hasStars = (catProg?.stars_earned ?? 0) > 0;
          const isCategoryMastered = catProg && catProg.stars_earned === catProg.stars_total;

          return (
            <div key={cat.id} style={{
              background: 'var(--theme-bg-card)',
              border: isCategoryMastered ? '1px solid rgba(16, 185, 129, 0.4)' : '1px solid var(--theme-border)',
              borderRadius: 'var(--theme-border-radius, 14px)',
              overflow: 'hidden',
              boxShadow: 'var(--theme-shadow)',
            }}>
              <button
                onClick={() => setExpanded(expanded === cat.id ? null : cat.id)}
                style={{
                  width: '100%', padding: '14px 16px',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  background: isCategoryMastered ? 'rgba(16, 185, 129, 0.05)' : 'none',
                  border: 'none', cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--theme-text)' }}>{cat.title}</div>
                  <div style={{ fontSize: 12, color: 'var(--theme-text-secondary)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span>{cat.topics.length} тем</span>
                    {hasStars && (
                      <span style={{ color: isCategoryMastered ? 'var(--theme-success)' : '#f59e0b', fontWeight: 600 }}>
                        • ⭐ {catProg?.stars_earned} из {catProg?.stars_total}
                      </span>
                    )}
                  </div>
                </div>
                <span style={{ color: 'var(--theme-text-muted)', fontSize: 18, transition: 'transform 0.2s', transform: expanded === cat.id ? 'rotate(180deg)' : 'none' }}>
                  ▾
                </span>
              </button>

              {expanded === cat.id && (
                <div style={{ borderTop: '1px solid var(--theme-border)' }}>
                  {cat.topics.map(topic => {
                    const topProg = progress?.topics?.[topic.id];
                    const stars = topProg?.stage_completed ?? 0;
                    const isMastered = topProg?.is_mastered ?? false;

                    return (
                      <button
                        key={topic.id}
                        onClick={() => onSelectTopic(topic)}
                        style={{
                          width: '100%', padding: '13px 18px',
                          display: 'flex', alignItems: 'center', gap: 12,
                          background: 'none', border: 'none',
                          borderBottom: '1px solid var(--theme-border)',
                          cursor: 'pointer', textAlign: 'left',
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--theme-text)', display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span>{topic.title}</span>
                            {isMastered && (
                              <span style={{ fontSize: 11, background: 'rgba(16, 185, 129, 0.15)', color: 'var(--theme-success)', padding: '1px 6px', borderRadius: 999 }}>
                                🏆 Мастер
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--theme-text-secondary)', marginTop: 2 }}>{topic.description}</div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 12, color: stars > 0 ? '#f59e0b' : 'var(--theme-text-muted)', letterSpacing: 1 }}>
                            {stars === 3 ? '⭐⭐⭐' : stars === 2 ? '⭐⭐☆' : stars === 1 ? '⭐☆☆' : '☆☆☆'}
                          </span>
                          <span style={{ color: 'var(--theme-text-muted)', fontSize: 16 }}>›</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// =====================
// Topic Detail (3 Ступени + AI Репетитор + Теория + Свободная тренировка)
// =====================

function TopicDetail({
  topic,
  topicProgress,
  onStartSprint,
  onStartInfinite,
}: {
  topic: GrammarTopic;
  topicProgress?: UserGrammarProgressDto;
  onStartSprint: (stage: number) => void;
  onStartInfinite: (type: ExerciseType) => void;
}) {
  const [theory, setTheory] = useState<Theory | null>(null);
  const [showTheory, setShowTheory] = useState(false);
  const [loadingTheory, setLoadingTheory] = useState(false);
  const [showInfinite, setShowInfinite] = useState(false);

  const stars = topicProgress?.stage_completed ?? 0;
  const isMastered = topicProgress?.is_mastered ?? false;

  const stages = [
    {
      stage: 1,
      star: '⭐',
      title: 'Ступень 1: База и интуиция',
      subtitle: 'Суть правила, утверждения, выбор формы глагола',
      score: topicProgress?.stage1_score,
      completed: stars >= 1,
    },
    {
      stage: 2,
      star: '⭐⭐',
      title: 'Ступень 2: Вопросы, отрицания и маркеры',
      subtitle: 'Вспомогательные глаголы, отрицания, маркеры времени',
      score: topicProgress?.stage2_score,
      completed: stars >= 2,
    },
    {
      stage: 3,
      star: '⭐⭐⭐',
      title: 'Ступень 3: Ловушки и «Битва времён»',
      subtitle: 'Прямой контраст с похожими временами и ловушки',
      score: topicProgress?.stage3_score,
      completed: stars >= 3,
    },
  ];

  const exerciseTypes: Array<{ type: ExerciseType; label: string; icon: string; color: string }> = [
    { type: 'fill_blanks',       label: 'Вставить слово',        icon: '✏️', color: 'var(--mode-input)' },
    { type: 'mc_quiz',           label: 'Выбрать ответ',         icon: '🎯', color: 'var(--mode-mc)' },
    { type: 'sentence_scramble', label: 'Составить предложение', icon: '🧩', color: 'var(--theme-primary)' },
    { type: 'correct_incorrect', label: 'Верно / Неверно',       icon: '✅', color: 'var(--theme-success)' },
  ];

  const handleShowTheory = async () => {
    setShowTheory(v => !v);
    if (!theory) {
      setLoadingTheory(true);
      try {
        const data = await api.get<Theory>(`/grammar/theory/${topic.id}`);
        setTheory(data);
      } catch (err) {
        captureError(err, { context: 'GrammarPage.loadTheory', topic_id: topic.id });
      } finally { setLoadingTheory(false); }
    }
  };

  return (
    <div style={{ padding: '20px 16px', maxWidth: 480, margin: '0 auto' }}>
      <BackButton />

      {/* Шапка темы */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: 'var(--theme-text)' }}>
            {topic.title}
          </h2>
          <div style={{
            fontSize: 13,
            fontWeight: 700,
            color: isMastered ? 'var(--theme-success)' : stars > 0 ? '#f59e0b' : 'var(--theme-text-muted)',
            background: isMastered ? 'rgba(16, 185, 129, 0.15)' : stars > 0 ? 'rgba(245, 158, 11, 0.15)' : 'rgba(255, 255, 255, 0.06)',
            padding: '3px 8px',
            borderRadius: 8,
            whiteSpace: 'nowrap',
          }}>
            {isMastered ? '🏆 Мастер темы' : stars > 0 ? `⭐ ${stars} / 3` : '☆☆☆'}
          </div>
        </div>
        <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--theme-text-secondary)', lineHeight: 1.4 }}>
          {topic.description}
        </p>
      </div>

      {/* AI-Репетитор по теме */}
      <AiTutorBox topic={topic} />

      {/* Теория темы */}
      <button
        onClick={handleShowTheory}
        style={{
          width: '100%', padding: '12px 16px', marginBottom: 18,
          background: 'var(--theme-bg-card)', border: '1px solid var(--theme-border)',
          borderRadius: 'var(--theme-border-radius, 14px)', color: 'var(--theme-text)',
          fontSize: 14, cursor: 'pointer', textAlign: 'left',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}
      >
        <span style={{ fontWeight: 600 }}>📖 {loadingTheory ? 'Загрузка шпаргалки...' : 'Шпаргалка и правила темы'}</span>
        <span style={{ transform: showTheory ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▾</span>
      </button>

      {showTheory && theory && (
        <div style={{
          background: 'var(--theme-bg-elevated)', border: '1px solid var(--theme-border)',
          borderRadius: 'var(--theme-border-radius, 14px)', padding: '16px', marginBottom: 18,
        }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--theme-text)', marginBottom: 12 }}>{theory.title}</div>
          {theory.rules.map((r, i) => (
            <div key={i} style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 13, color: 'var(--theme-text)', fontWeight: 600 }}>{r.rule}</div>
              <div style={{ fontSize: 12, color: 'var(--theme-primary)', fontStyle: 'italic', marginTop: 3 }}>{r.example}</div>
            </div>
          ))}
          {theory.tip && (
            <div style={{ borderTop: '1px solid var(--theme-border)', paddingTop: 10, marginTop: 10, fontSize: 12, color: 'var(--theme-text-secondary)' }}>
              💡 {theory.tip}
            </div>
          )}
        </div>
      )}

      {/* 3 СТУПЕНИ МАСТЕРСТВА */}
      <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--theme-text-muted)', textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 10 }}>
        Ступени мастерства (по 10 заданий)
      </div>

      <div style={{ display: 'grid', gap: 10, marginBottom: 20 }}>
        {stages.map((st) => (
          <div
            key={st.stage}
            style={{
              background: st.completed ? 'rgba(16, 185, 129, 0.06)' : 'var(--theme-bg-card)',
              border: `1.5px solid ${st.completed ? 'rgba(16, 185, 129, 0.4)' : 'var(--theme-border)'}`,
              borderRadius: 16,
              padding: '14px 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
            }}
          >
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                <span style={{ fontSize: 13 }}>{st.star}</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--theme-text)' }}>
                  {st.title}
                </span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--theme-text-secondary)', lineHeight: 1.3 }}>
                {st.subtitle}
              </div>
            </div>

            <div>
              <button
                type="button"
                onClick={() => onStartSprint(st.stage)}
                style={{
                  padding: '9px 14px',
                  background: st.completed ? 'rgba(16, 185, 129, 0.2)' : 'var(--theme-primary)',
                  color: st.completed ? 'var(--theme-success)' : '#ffffff',
                  border: 'none',
                  borderRadius: 10,
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                {st.completed ? `✓ ${st.score || 10}/10` : 'Начать →'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Бесконечный тренажёр (Свободная практика) */}
      <div style={{ borderTop: '1px solid var(--theme-border)', paddingTop: 14 }}>
        <button
          onClick={() => setShowInfinite(v => !v)}
          style={{
            width: '100%',
            background: 'none',
            border: 'none',
            color: 'var(--theme-text-secondary)',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 0',
          }}
        >
          <span>🔄 Свободная бесконечная тренировка</span>
          <span style={{ transform: showInfinite ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▾</span>
        </button>

        {showInfinite && (
          <div style={{ display: 'grid', gap: 8, marginTop: 10 }}>
            {exerciseTypes.map(({ type, label, icon, color }) => (
              <button
                key={type}
                onClick={() => onStartInfinite(type)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
                  background: 'var(--theme-bg-elevated)', border: `1px solid var(--theme-border)`,
                  borderRadius: 12, color: 'var(--theme-text)', cursor: 'pointer', textAlign: 'left',
                }}
              >
                <div style={{
                  width: 36, height: 36, borderRadius: 8,
                  background: `${color}22`, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 18, flexShrink: 0,
                }}>
                  {icon}
                </div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{label}</div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


// =====================
// Fill Blanks Exercise
// =====================

function FillBlanksExercise({ exercise, onNext }: { exercise: Exercise; onNext: (correct: boolean) => void }) {
  const [input, setInput] = useState('');
  const [result, setResult] = useState<{ correct: boolean; shown: boolean } | null>(null);
  const { sentence, correct_answer, hint, explanation_ru, alternatives } = exercise.data;

  const handleSubmit = () => {
    if (!input.trim() || result) return;
    const userAnswer = input.trim().toLowerCase();
    const correct = userAnswer === correct_answer.toLowerCase() ||
      (alternatives || []).some((a: string) => a.toLowerCase() === userAnswer);
    setResult({ correct, shown: true });
  };

  const parts = sentence.split('___');

  return (
    <div style={{ padding: '20px 16px' }}>
      <div style={{ fontSize: 11, color: 'var(--theme-text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 16 }}>
        ✏️ Вставь слово
      </div>

      <div style={{
        background: 'var(--theme-bg-elevated)', borderRadius: 16,
        padding: '24px 20px', marginBottom: 16, textAlign: 'center',
      }}>
        <div style={{ fontSize: 18, color: 'var(--theme-text)', lineHeight: 1.7 }}>
          {parts[0]}
          <span style={{
            display: 'inline-block', minWidth: 80, borderBottom: `2px solid ${result
              ? result.correct ? 'var(--theme-success)' : 'var(--theme-error)'
              : 'var(--theme-primary)'}`,
            margin: '0 6px', color: result ? (result.correct ? 'var(--theme-success)' : 'var(--theme-error)') : 'var(--theme-primary)',
            fontWeight: 700,
          }}>
            {result ? correct_answer : (input || '\u00A0')}
          </span>
          {parts[1]}
        </div>
      </div>

      {hint && !result && (
        <div style={{ fontSize: 12, color: 'var(--theme-text-secondary)', marginBottom: 12, textAlign: 'center' }}>
          💡 {hint}
        </div>
      )}

      {!result && (
        <>
          <input
            type="text" value={input} onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSubmit()}
            placeholder="Введите слово..." autoFocus
            style={{
              width: '100%', padding: '12px 14px', marginBottom: 10,
              background: 'var(--theme-bg-elevated)', border: '1px solid var(--theme-border)',
              borderRadius: 10, color: 'var(--theme-text)', fontSize: 16,
              outline: 'none', textAlign: 'center', boxSizing: 'border-box',
            }}
          />
          <button
            onClick={handleSubmit}
            disabled={!input.trim()}
            style={{
              width: '100%', padding: '13px',
              background: input.trim() ? 'var(--mode-input)' : 'var(--theme-border)',
              color: 'white', border: 'none', borderRadius: 10,
              fontSize: 15, fontWeight: 600, cursor: input.trim() ? 'pointer' : 'not-allowed',
            }}
          >Проверить</button>
        </>
      )}

      {result && (
        <ExerciseFeedback
          correct={result.correct}
          correctAnswer={correct_answer}
          explanation={explanation_ru}
          onNext={() => onNext(result.correct)}
        />
      )}
    </div>
  );
}

// =====================
// MC Quiz Exercise
// =====================

function McQuizExercise({ exercise, onNext }: { exercise: Exercise; onNext: (correct: boolean) => void }) {
  const [selected, setSelected] = useState<number | null>(null);
  const { question, options, correct_index, explanation_ru } = exercise.data;

  const handleSelect = (idx: number) => {
    if (selected !== null) return;
    setSelected(idx);
  };

  return (
    <div style={{ padding: '20px 16px' }}>
      <div style={{ fontSize: 11, color: 'var(--theme-text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 16 }}>
        🎯 Выбери правильный ответ
      </div>

      <div style={{
        background: 'var(--theme-bg-elevated)', borderRadius: 16,
        padding: '20px', marginBottom: 16, fontSize: 16, color: 'var(--theme-text)',
        lineHeight: 1.5, textAlign: 'center',
      }}>
        {question}
      </div>

      <div style={{ display: 'grid', gap: 8 }}>
        {(options as string[]).map((option, idx) => {
          const isSelected = selected === idx;
          const isCorrect = idx === correct_index;
          let bg = 'var(--theme-bg-card)', border = 'var(--theme-border)', color = 'var(--theme-text)';
          if (selected !== null) {
            if (isCorrect) { bg = 'rgba(0,230,118,0.1)'; border = 'var(--theme-success)'; color = 'var(--theme-success)'; }
            else if (isSelected) { bg = 'rgba(255,82,82,0.1)'; border = 'var(--theme-error)'; color = 'var(--theme-error)'; }
          }
          return (
            <button key={idx} onClick={() => handleSelect(idx)} style={{
              padding: '13px 16px', background: bg, border: `1px solid ${border}`,
              borderRadius: 10, color, fontSize: 14, fontWeight: 500,
              cursor: selected !== null ? 'default' : 'pointer', textAlign: 'left', minHeight: '44px',
            }}>
              {option}
            </button>
          );
        })}
      </div>

      {selected !== null && (
        <ExerciseFeedback
          correct={selected === correct_index}
          correctAnswer={options[correct_index]}
          explanation={explanation_ru}
          onNext={() => onNext(selected === correct_index)}
        />
      )}
    </div>
  );
}

// =====================
// Sentence Scramble Exercise
// =====================

function SentenceScrambleExercise({ exercise, onNext }: { exercise: Exercise; onNext: (correct: boolean) => void }) {
  const { words: originalWords, correct_sentence, translation_hint, explanation_ru } = exercise.data;
  const [bank, setBank] = useState<string[]>([...(originalWords as string[])]);
  const [answer, setAnswer] = useState<string[]>([]);
  const [result, setResult] = useState<{ correct: boolean } | null>(null);

  const addWord = (word: string, idx: number) => {
    if (result) return;
    setAnswer(a => [...a, word]);
    setBank(b => b.filter((_, i) => i !== idx));
  };

  const removeWord = (word: string, idx: number) => {
    if (result) return;
    setBank(b => [...b, word]);
    setAnswer(a => a.filter((_, i) => i !== idx));
  };

  const handleCheck = () => {
    const userSentence = answer.join(' ');
    const correct = userSentence.trim().toLowerCase() === correct_sentence.trim().toLowerCase();
    setResult({ correct });
  };

  return (
    <div style={{ padding: '20px 16px' }}>
      <div style={{ fontSize: 11, color: 'var(--theme-text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 16 }}>
        🧩 Составь предложение
      </div>

      <div style={{ fontSize: 13, color: 'var(--theme-text-secondary)', textAlign: 'center', marginBottom: 12 }}>
        {translation_hint}
      </div>

      {/* Answer area */}
      <div style={{
        minHeight: 56, background: 'var(--theme-bg-elevated)',
        border: `1px solid ${result ? (result.correct ? 'var(--theme-success)' : 'var(--theme-error)') : 'var(--theme-border)'}`,
        borderRadius: 12, padding: '10px 12px', marginBottom: 12,
        display: 'flex', flexWrap: 'wrap', gap: 6,
      }}>
        {answer.length === 0 && (
          <span style={{ color: 'var(--theme-text-muted)', fontSize: 13, alignSelf: 'center' }}>
            Нажимай слова ниже...
          </span>
        )}
        {answer.map((w, i) => (
          <button
            key={i}
            onClick={() => removeWord(w, i)}
            style={{
              padding: '6px 12px', background: 'var(--theme-primary)22',
              border: '1px solid var(--theme-primary)', borderRadius: 8,
              color: 'var(--theme-primary)', fontSize: 14, cursor: result ? 'default' : 'pointer',
            }}
          >
            {w}
          </button>
        ))}
      </div>

      {/* Word bank */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
        {bank.map((w, i) => (
          <button
            key={i}
            onClick={() => addWord(w, i)}
            style={{
              padding: '8px 14px',
              background: 'var(--theme-bg-card)', border: '1px solid var(--theme-border)',
              borderRadius: 8, color: 'var(--theme-text)', fontSize: 14,
              cursor: result ? 'default' : 'pointer',
            }}
          >
            {w}
          </button>
        ))}
      </div>

      {!result && (
        <button
          onClick={handleCheck}
          disabled={answer.length === 0}
          style={{
            width: '100%', padding: '13px',
            background: answer.length ? 'var(--theme-primary)' : 'var(--theme-border)',
            color: 'var(--theme-primary-text)', border: 'none', borderRadius: 10,
            fontSize: 15, fontWeight: 600, cursor: answer.length ? 'pointer' : 'not-allowed',
          }}
        >Проверить</button>
      )}

      {result && (
        <ExerciseFeedback
          correct={result.correct}
          correctAnswer={correct_sentence}
          explanation={explanation_ru}
          onNext={() => onNext(result.correct)}
        />
      )}
    </div>
  );
}

// =====================
// Correct / Incorrect Exercise
// =====================

function CorrectIncorrectExercise({ exercise, onNext }: { exercise: Exercise; onNext: (correct: boolean) => void }) {
  const [selected, setSelected] = useState<boolean | null>(null);
  const { sentence, is_correct, correction, explanation_ru } = exercise.data;

  const handleAnswer = (answer: boolean) => {
    if (selected !== null) return;
    setSelected(answer);
  };

  const userCorrect = selected !== null && selected === is_correct;

  return (
    <div style={{ padding: '20px 16px' }}>
      <div style={{ fontSize: 11, color: 'var(--theme-text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 16 }}>
        ✅ Верно или Неверно?
      </div>

      <div style={{
        background: 'var(--theme-bg-elevated)', borderRadius: 16,
        padding: '24px 20px', marginBottom: 24, textAlign: 'center',
        fontSize: 18, color: 'var(--theme-text)', lineHeight: 1.5,
        fontStyle: 'italic',
      }}>
        "{sentence}"
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {[true, false].map(value => {
          const label = value ? '✅ Верно' : '❌ Неверно';
          const isSelected = selected === value;
          const isActualAnswer = value === is_correct;
          let bg = 'var(--theme-bg-card)', border = 'var(--theme-border)', color = 'var(--theme-text)';
          if (selected !== null) {
            if (isActualAnswer) { bg = 'rgba(0,230,118,0.1)'; border = 'var(--theme-success)'; color = 'var(--theme-success)'; }
            else if (isSelected) { bg = 'rgba(255,82,82,0.1)'; border = 'var(--theme-error)'; color = 'var(--theme-error)'; }
          }
          return (
            <button key={String(value)} onClick={() => handleAnswer(value)} style={{
              padding: '16px', background: bg, border: `1px solid ${border}`,
              borderRadius: 12, color, fontSize: 16, fontWeight: 600,
              cursor: selected !== null ? 'default' : 'pointer', minHeight: '52px',
            }}>
              {label}
            </button>
          );
        })}
      </div>

      {selected !== null && (
        <ExerciseFeedback
          correct={userCorrect}
          correctAnswer={is_correct ? `Верно. ${correction || ''}` : `Неверно. Правильно: "${correction}"`}
          explanation={explanation_ru}
          onNext={() => onNext(userCorrect)}
        />
      )}
    </div>
  );
}

// =====================
// Shared Feedback Block
// =====================

function ExerciseFeedback({ correct, correctAnswer, explanation, onNext }: {
  correct: boolean;
  correctAnswer: string;
  explanation: string;
  onNext: () => void;
}) {
  return (
    <div style={{ marginTop: 16 }}>
      <div style={{
        padding: '12px 16px',
        background: correct ? 'rgba(0,230,118,0.1)' : 'rgba(255,82,82,0.1)',
        border: `1px solid ${correct ? 'var(--theme-success)' : 'var(--theme-error)'}`,
        borderRadius: 10, marginBottom: 12,
      }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: correct ? 'var(--theme-success)' : 'var(--theme-error)', marginBottom: 4 }}>
          {correct ? '✓ Правильно!' : '✗ Неправильно'}
        </div>
        {!correct && (
          <div style={{ fontSize: 13, color: 'var(--theme-text-secondary)' }}>
            Правильный ответ: <strong style={{ color: 'var(--theme-text)' }}>{correctAnswer}</strong>
          </div>
        )}
        {explanation && (
          <div style={{ fontSize: 12, color: 'var(--theme-text-secondary)', marginTop: 8, lineHeight: 1.5 }}>
            {explanation}
          </div>
        )}
      </div>
      <button
        onClick={onNext}
        style={{
          width: '100%', padding: '13px',
          background: 'var(--theme-primary)', color: 'var(--theme-primary-text)',
          border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: 'pointer',
        }}
      >
        Следующее →
      </button>
    </div>
  );
}

// =====================
// Exercise Runner
// =====================

function ExerciseRunner({ topic, type, onExit }: {
  topic: GrammarTopic;
  type: ExerciseType;
  onExit: () => void;
}) {
  const [exercise, setExercise] = useState<Exercise | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [correctCount, setCorrectCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState('');

  const loadExercise = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await api.post<Exercise>('/grammar/exercise', { topic_id: topic.id, type });
      setExercise(data);
    } catch (e: any) {
      captureError(e, { context: 'GrammarPage.loadExercise', topic_id: topic.id, type });
      setError(e.message || 'Ошибка генерации упражнения');
    } finally {
      setIsLoading(false);
    }
  }, [topic.id, type]);

  useEffect(() => { loadExercise(); }, [loadExercise]);

  const handleNext = (correct: boolean) => {
    setCorrectCount(c => c + (correct ? 1 : 0));
    setTotalCount(t => t + 1);
    loadExercise();
  };

  const accuracy = totalCount > 0 ? Math.round((correctCount / totalCount) * 100) : null;

  return (
    <>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 16px',
        background: 'var(--theme-bg-elevated)', borderBottom: '1px solid var(--theme-border)',
      }}>
        <button onClick={onExit} style={{ background: 'none', border: 'none', color: 'var(--theme-text-secondary)', fontSize: 20, cursor: 'pointer', padding: 4 }}>✕</button>
        <div style={{ fontSize: 13, color: 'var(--theme-text)' }}>{topic.title}</div>
        {accuracy !== null && (
          <div style={{ fontSize: 13, color: 'var(--theme-success)', fontWeight: 600 }}>{accuracy}%</div>
        )}
        {accuracy === null && <div style={{ width: 32 }} />}
      </div>

      {isLoading && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: 'var(--theme-text-secondary)' }}>
          Генерация упражнения... 🤖
        </div>
      )}

      {error && (
        <div style={{ padding: '24px 16px', textAlign: 'center' }}>
          <div style={{ color: 'var(--theme-error)', marginBottom: 16 }}>{error}</div>
          <button onClick={loadExercise} style={{ padding: '12px 24px', background: 'var(--theme-primary)', color: 'var(--theme-primary-text)', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 14 }}>
            Попробовать снова
          </button>
        </div>
      )}

      {!isLoading && !error && exercise && (
        <>
          {exercise.type === 'fill_blanks' && <FillBlanksExercise exercise={exercise} onNext={handleNext} />}
          {exercise.type === 'mc_quiz' && <McQuizExercise exercise={exercise} onNext={handleNext} />}
          {exercise.type === 'sentence_scramble' && <SentenceScrambleExercise exercise={exercise} onNext={handleNext} />}
          {exercise.type === 'correct_incorrect' && <CorrectIncorrectExercise exercise={exercise} onNext={handleNext} />}
        </>
      )}
    </>
  );
}

// =====================
// Recommendations Panel
// =====================

function RecommendationsPanel({ onSelectTopic, allTopics }: {
  onSelectTopic: (topic: GrammarTopic) => void;
  allTopics: GrammarTopic[];
}) {
  const [recs, setRecs] = useState<{ recommendations: any[]; general_advice: string } | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const loadRecs = () => {
    setIsLoading(true);
    api.get<any>('/grammar/recommendations')
      .then(setRecs)
      .catch((err) => captureError(err, { context: 'GrammarPage.recommendations' }))
      .finally(() => setIsLoading(false));
  };

  if (!recs && !isLoading) {
    return (
      <div style={{ padding: '0 16px 16px' }}>
        <button
          onClick={loadRecs}
          style={{
            width: '100%', padding: '12px', background: 'rgba(var(--theme-primary-rgb, 79, 70, 229), 0.08)',
            border: '1px solid var(--theme-border)', borderRadius: 10, cursor: 'pointer',
            fontSize: 13, color: 'var(--theme-primary)', fontWeight: 500,
          }}
        >
          🎯 Получить рекомендации AI
        </button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div style={{ padding: '0 16px 16px', textAlign: 'center', fontSize: 13, color: 'var(--theme-text-secondary)' }}>
        ⏳ Анализирую ваши ошибки...
      </div>
    );
  }

  if (recs && recs.recommendations.length === 0) {
    return (
      <div style={{ padding: '0 16px 16px', textAlign: 'center', fontSize: 13, color: 'var(--theme-text-secondary)' }}>
        {recs.general_advice || 'Пока для вас нет рекомендаций'}
      </div>
    );
  }

  if (!recs) return null;

  return (
    <div style={{ padding: '0 16px 16px' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--theme-text-muted)', textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 10 }}>
        🎯 Рекомендую изучить
      </div>
      {recs.recommendations.map((rec: any) => {
        const topic = allTopics.find(t => t.id === rec.topic_id);
        if (!topic) return null;
        return (
          <button
            key={rec.topic_id}
            onClick={() => onSelectTopic(topic)}
            style={{
              width: '100%', padding: '12px 14px', marginBottom: 8,
              background: 'var(--theme-bg-card)', border: '1px solid var(--theme-border)',
              borderLeft: `3px solid ${rec.priority === 'high' ? 'var(--theme-error)' : 'var(--theme-warning)'}`,
              borderRadius: 10, textAlign: 'left', cursor: 'pointer',
            }}
          >
            <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--theme-text)' }}>{topic.title}</div>
            <div style={{ fontSize: 11, color: 'var(--theme-text-secondary)', marginTop: 2 }}>{rec.reason}</div>
          </button>
        );
      })}
      {recs.general_advice && (
        <div style={{ fontSize: 12, color: 'var(--theme-text-secondary)', fontStyle: 'italic', marginTop: 8 }}>
          💬 {recs.general_advice}
        </div>
      )}
    </div>
  );
}

// =====================
// Main GrammarPage
// =====================

export function GrammarPage() {
  const [categories, setCategories] = useState<GrammarCategory[]>([]);
  const [progress, setProgress] = useState<GrammarProgressResponse | null>(null);
  const [selectedTopic, setSelectedTopic] = useState<GrammarTopic | null>(null);
  const [activeStage, setActiveStage] = useState<number | null>(null);
  const [activeExercise, setActiveExercise] = useState<ExerciseType | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const allTopics = categories.flatMap(c => c.topics);

  const isTabActive = useIsTabActive();

  const loadData = useCallback(() => {
    Promise.all([
      api.get<GrammarCategory[]>('/grammar/topics').catch(() => []),
      api.get<GrammarProgressResponse>('/grammar/progress').catch(() => null),
    ]).then(([cats, prog]) => {
      if (cats && cats.length > 0) setCategories(cats);
      if (prog) setProgress(prog);
    }).catch((err) => {
      captureError(err, { context: 'GrammarPage.loadData' });
    }).finally(() => {
      setIsLoading(false);
    });
  }, []);

  // Фоновое тихое обновление тем и прогресса грамматики
  useOnTabActive(() => {
    loadData();
  });

  // Если открыт спринт, упражнение или тема — свайп назад или BackButton Telegram закрывает их
  useBackHandler(() => {
    if (activeStage) {
      setActiveStage(null);
    } else if (activeExercise) {
      setActiveExercise(null);
    } else if (selectedTopic) {
      setSelectedTopic(null);
    }
  }, isTabActive && Boolean(selectedTopic || activeStage || activeExercise), 5);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (isLoading && categories.length === 0) {
    return (
      <Layout>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: 'var(--theme-text-secondary)' }}>
          Загрузка...
        </div>
      </Layout>
    );
  }

  // Активный зачётный спринт (10 заданий ступени)
  if (selectedTopic && activeStage) {
    return (
      <Layout showNav={false}>
        <SprintRunner
          topic={selectedTopic}
          stage={activeStage}
          onExit={() => setActiveStage(null)}
          onCompleted={(res) => {
            loadData(); // перезагрузить прогресс в фоне
          }}
          onNextStage={(next) => {
            setActiveStage(next);
          }}
        />
      </Layout>
    );
  }

  // Свободное упражнение (бесконечный режим)
  if (selectedTopic && activeExercise) {
    return (
      <Layout showNav={false}>
        <ExerciseRunner
          topic={selectedTopic}
          type={activeExercise}
          onExit={() => setActiveExercise(null)}
        />
      </Layout>
    );
  }

  // Детальная страница темы
  if (selectedTopic) {
    return (
      <Layout>
        <TopicDetail
          topic={selectedTopic}
          topicProgress={progress?.topics?.[selectedTopic.id]}
          onStartSprint={(stage) => setActiveStage(stage)}
          onStartInfinite={(type) => setActiveExercise(type)}
        />
      </Layout>
    );
  }

  // Список категорий
  return (
    <Layout>
      <BackButton />
      <RecommendationsPanel onSelectTopic={setSelectedTopic} allTopics={allTopics} />
      <CategoryList
        categories={categories}
        progress={progress}
        onSelectTopic={setSelectedTopic}
      />
    </Layout>
  );
}
